import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import CloudIcon from "@mui/icons-material/Cloud";
import DnsIcon from "@mui/icons-material/Dns";
import HubIcon from "@mui/icons-material/Hub";
import StorageIcon from "@mui/icons-material/Storage";
import PageTitle from "../../components/common/PageTitle";
import { connectorService } from "../../api/services/connectorService";
import { CONNECTOR_PROVIDER_TILES } from "../../utils/connectorDashboardConfig";
import type { ConnectorProviderType } from "../../types/connectors";

const providerIcons: Record<ConnectorProviderType, React.ReactNode> = {
  "apigee-edge": <CloudIcon sx={{ fontSize: 40, color: "primary.main" }} />,
  "apigee-opdk": <StorageIcon sx={{ fontSize: 40, color: "primary.main" }} />,
  "apigee-hybrid": <HubIcon sx={{ fontSize: 40, color: "primary.main" }} />,
  "apigee-x": <DnsIcon sx={{ fontSize: 40, color: "primary.main" }} />,
};

const ConnectorsDashboardPage: React.FC = () => {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const connectors = await connectorService.getConnectors();
        const next: Record<string, number> = {};
        for (const tile of CONNECTOR_PROVIDER_TILES) {
          next[tile.id] = connectors.filter(
            (c) => c.providerType === tile.id,
          ).length;
        }
        setCounts(next);
      } catch {
        /* counts optional */
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle
        pageTitle="Connectors"
        pageDescription="Register and manage integrations with API management platforms. Select a provider to add a new connector."
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        <Grid container spacing={3}>
          {CONNECTOR_PROVIDER_TILES.map((tile) => (
            <Grid item xs={12} sm={6} md={6} key={tile.id}>
              <Card
                variant="outlined"
                sx={{
                  height: "100%",
                  borderRadius: 2,
                  transition: "box-shadow 0.2s",
                  "&:hover": { boxShadow: 4 },
                }}
              >
                <CardActionArea
                  component={RouterLink}
                  to={tile.addPath}
                  sx={{ height: "100%", alignItems: "stretch" }}
                >
                  <CardContent sx={{ textAlign: "center", py: 3 }}>
                    {providerIcons[tile.id]}
                    <Typography variant="h6" fontWeight={600} sx={{ mt: 2 }}>
                      {tile.title}
                    </Typography>
                    {!loading && counts[tile.id] != null && (
                      <Typography
                        variant="caption"
                        color="primary"
                        display="block"
                      >
                        {counts[tile.id]} connector
                        {counts[tile.id] === 1 ? "" : "s"}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {tile.description}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 2 }}
                    >
                      Click to add connector
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default ConnectorsDashboardPage;
